import React, { useCallback, useState, ReactNode } from 'react';
import { ToastType } from '../components/Atoms/Toast/Toast';
import { ToastContext } from './toastContextValue';

export interface ToastData {
    id: string;
    message: string;
    type: ToastType;
    duration?: number;
}

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<ToastData[]>([]);

    const showToast = useCallback((message: string, type: ToastType, duration = 3000) => {
        setToasts((prev) => {
            // Dedupe: if an identical toast is already on screen, don't stack
            // another. Prevents bursts of the same error (connection retries,
            // parallel failed requests) from flooding the screen with duplicates.
            if (prev.some((toast) => toast.message === message && toast.type === type)) {
                return prev;
            }
            const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            return [...prev, { id, message, type, duration }];
        });
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ showToast, removeToast }}>
            {children}
            <div className="fixed top-0 left-0 right-0 z-[9999] pointer-events-none flex flex-col items-center">
                {/* Container for toasts so they stack properly if needed, although fixed positioning in Toast handles basic positioning.
                     Ideally, we map them here. The Toast component itself has fixed positioning which might overlap if we just map them.
                     Let's adjust Toast.tsx logic slightly or handle stacking here.
                     Actually, with the current Toast implementation having `fixed` position, they will overlap.
                     Better to have a fixed container here and let Toast just be a block.
                 */}
                {/* Re-evaluating Toast.tsx: it has fixed positioning.
                     Lets override that here or render them in a container that handles layout?
                     The current Toast.tsx uses fixed positioning. Let's fix that in next step or use a container here.
                     Wait, I already wrote Toast.tsx with fixed positioning.
                     Each toast has `top-4`.
                     Let's update Toast.tsx to NOT be fixed, and put them in a fixed container here.
                  */}
                {/*
                    Checking previous file write:
                    I wrote Toast.tsx with fixed positioning.
                    I will update Toast.tsx to be relative, and put them in a fixed container here.
                 */}
            </div>
            {/* The fixed container approach */}
            <div className="fixed top-0 left-0 w-full flex flex-col items-center pointer-events-none z-[9999] gap-2" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}>
                {toasts.map((toast) => (
                    <ToastWrapper key={toast.id} {...toast} onClose={removeToast} />
                ))}
            </div>
        </ToastContext.Provider>
    );
};

// Wrapper ensuring the Toast component behaves correctly inside the container
// Since I can't easily edit Toast.tsx in this same turn without complexity, 
// I'll make a wrapper or just rely on the fact that I can edit Toast.tsx in next step.
// Actually, Toast.tsx uses 'fixed' class. That is not ideal for stacking.
// I should update Toast.tsx.
// For now, I will write this file, then update Toast.tsx.

import { Toast as ToastComponent } from '../components/Atoms/Toast/Toast';

const ToastWrapper: React.FC<ToastData & { onClose: (id: string) => void }> = (props) => {
    // We need to pass a prop to Toast to tell it NOT to be fixed? 
    // Or just re-write Toast.tsx.
    // I will rewrite Toast.tsx in next step.
    return <ToastComponent {...props} />;
};
